import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 💰 Wiki - Seção Comissões
 * 
 * Documentação completa sobre o sistema de comissões da plataforma.
 * Explica como funciona o cálculo, quem recebe, e como configurar.
 * Voltada para produtores (não técnica).
 */
const ComissoesWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:cash-multiple" />
          <span>Sistema de Comissões</span>
        </div>
        <p className={styles.heroText}>
          Entenda como funciona o sistema de distribuição de valores das suas vendas.
          Comissões justas e automáticas para todos os envolvidos.
        </p>
      </section>

      {/* O que são Comissões */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são Comissões?</span>
          <p className={styles.sectionSubtitle}>
            Entenda como seu dinheiro é distribuído
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>Comissão</strong> é a parte do valor da venda que é distribuída 
              para cada pessoa ou empresa envolvida no processo: <strong>afiliados</strong> que 
              divulgam seus produtos, <strong>fornecedores</strong> que fabricam, <strong>representantes 
              logísticos</strong> que entregam, e claro, <strong>você</strong>, o produtor.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              A Pagmus calcula e distribui tudo <strong>automaticamente</strong> sempre que 
              uma venda é confirmada. Você não precisa fazer nada manualmente!
            </p>
          </div>
        </div>
      </section>

      {/* Quem Recebe Comissão */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Quem Recebe Comissão?</span>
          <p className={styles.sectionSubtitle}>
            Todos os participantes envolvidos na venda
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Taxa Fixa</div>
                <span className={styles.cardTitle}>Pagmus (Plataforma)</span>
                <p>
                  A plataforma cobra uma <strong>taxa fixa de 4%</strong> sobre cada venda.
                  Essa taxa cobre custos de infraestrutura, segurança e manutenção.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> Venda de R$ 1.000 → Taxa R$ 40</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:account-cash" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Percentual</div>
                <span className={styles.cardTitle}>Afiliados e Vendedores</span>
                <p>
                  Afiliados e vendedores recebem um <strong>percentual</strong> que você 
                  define ao aprovar a afiliação (ex: 25%).
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> 25% de R$ 960 → R$ 240</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSupplier}`}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSupplier}`}>Valor Fixo</div>
                <span className={styles.cardTitle}>Fornecedores</span>
                <p>
                  Fornecedores de produtos físicos recebem um <strong>valor fixo em reais</strong> 
                  que você define ao cadastrá-los no plano (ex: R$ 10,00).
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> Valor fixo de R$ 10 por venda</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconLogistics}`}>
                <Icon icon="mdi:truck-delivery" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeLogistics}`}>Valor Fixo</div>
                <span className={styles.cardTitle}>Representante Logístico</span>
                <p>
                  Responsável pela entrega recebe um <strong>valor fixo</strong> que 
                  você define no plano (ex: R$ 5,00).
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> Valor fixo de R$ 5 por entrega</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.participantCard} style={{ borderLeft: '4px solid #10b981' }}>
              <div className={`${styles.cardIcon} ${styles.iconProducer}`}>
                <Icon icon="mdi:crown" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeProducer}`}>Valor Residual</div>
                <span className={styles.cardTitle}>Você (Produtor)</span>
                <p>
                  Você recebe o que <strong>sobra após todas as deduções</strong>. 
                  É o valor que resta depois de pagar taxa, afiliado, fornecedor e representante.
                </p>
                <div className={styles.exampleBox} style={{ background: '#ecfdf5' }}>
                  <Icon icon="mdi:cash-check" style={{ color: '#10b981' }} />
                  <span style={{ color: '#4B5563' }}>
                    <strong style={{ color: '#111827' }}>Exemplo: </strong> 
                    Venda de R$ 1.000 - R$ 40 (taxa) - R$ 240 (afiliado) - R$ 10 (fornecedor) - R$ 5 (rep. logístico) =
                    <strong style={{ color: '#111827', }}> R$ 705 para você</strong>
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona o Cálculo */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona o Cálculo?</span>
          <p className={styles.sectionSubtitle}>
            Ordem de dedução das comissões
          </p>
        </div>

        <div className={styles.calculationFlow}>
          <div className={styles.flowStep}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Valor da Venda</span>
              <span className={styles.stepValue}>R$ 1.000,00</span>
              <p>Cliente pagou pelo produto</p>
            </div>
          </div>

          <Icon icon="mdi:arrow-down" className={styles.flowArrow} />

          <div className={styles.flowStep}>
            <div className={`${styles.stepNumber} ${styles.stepPlatform}`}>2</div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Taxa Pagmus (4%)</span>
              <span className={styles.stepValue}>- R$ 40,00</span>
              <p>Restante: R$ 960,00</p>
            </div>
          </div>

          <Icon icon="mdi:arrow-down" className={styles.flowArrow} />

          <div className={styles.flowStep}>
            <div className={`${styles.stepNumber} ${styles.stepAffiliate}`}>3</div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Afiliado (25%)</span>
              <span className={styles.stepValue}>- R$ 240,00</span>
              <p>Restante: R$ 720,00</p>
            </div>
          </div>

          <Icon icon="mdi:arrow-down" className={styles.flowArrow} />

          <div className={styles.flowStep}>
            <div className={`${styles.stepNumber} ${styles.stepSupplier}`}>4</div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Fornecedor (fixo)</span>
              <span className={styles.stepValue}>- R$ 10,00</span>
              <p>Restante: R$ 710,00</p>
            </div>
          </div>

          <Icon icon="mdi:arrow-down" className={styles.flowArrow} />

          <div className={styles.flowStep}>
            <div className={`${styles.stepNumber} ${styles.stepLogistics}`}>5</div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Rep. Logístico (fixo)</span>
              <span className={styles.stepValue}>- R$ 5,00</span>
              <p>Restante: R$ 705,00</p>
            </div>
          </div>

          <Icon icon="mdi:arrow-down" className={styles.flowArrow} />

          <div className={`${styles.flowStep} ${styles.flowFinal}`}>
            <div className={`${styles.stepNumber} ${styles.stepProducer}`}>
              <Icon icon="mdi:crown" />
            </div>
            <div className={styles.stepContent}>
              <span className={styles.stepTitle}>Você recebe</span>
              <span className={`${styles.stepValue} ${styles.finalValue}`}>R$ 705,00</span>
              <p>Valor líquido do produtor</p>
            </div>
          </div>
        </div>
      </section>

      {/* Como Configurar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Configurar Comissões?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para definir os valores
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-plus" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Afiliados/Vendedores
                </span>
                <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Vá em <strong>Afiliados → Meus Afiliados</strong> e clique em 
                  "Ver detalhes" no afiliado. Na seção "Editar Afiliação", defina o 
                  <strong> percentual de comissão</strong> e salve.
                </p>
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(217, 119, 6, 0.05))',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.1)'
                }}>
                  <Icon icon="mdi:lightbulb" style={{ color: '#F59E0B', fontSize: '20px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem' }}>Comissões comuns: 10% a 30%</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Fornecedores
                </span>
                <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Ao <strong>editar um plano</strong> do produto, vá na aba 
                  "Fornecedores". Adicione até 3 fornecedores e defina o 
                  <strong> valor fixo em reais</strong> que cada um receberá.
                </p>
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(217, 119, 6, 0.05))',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.1)'
                }}>
                  <Icon icon="mdi:lightbulb" style={{ color: '#F59E0B', fontSize: '20px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem' }}>Limite: até 3 fornecedores por plano</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:truck-delivery" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.cardTitle}>
                  Representante Logístico
                </span>
                <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Ao <strong>editar um plano</strong> do produto, adicione um representante 
                  logístico e defina o <strong>valor fixo</strong> que ele receberá por entrega.
                </p>
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(217, 119, 6, 0.05))',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.1)'
                }}>
                  <Icon icon="mdi:lightbulb" style={{ color: '#F59E0B', fontSize: '20px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem' }}>Apenas 1 representante por plano</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Perguntas Frequentes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Dúvidas comuns sobre comissões
          </p>
        </div>

        <div className={styles.faqContainer}>
          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Quando as comissões são pagas?</span>
              <p className={styles.faqAnswer}>
                As comissões são <strong>calculadas automaticamente</strong> assim que 
                o pagamento é confirmado. Cada participante pode solicitar saque após 
                o período de liberação (ex: D+15, D+30), configurado nas suas políticas.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Posso mudar a comissão de um afiliado depois?</span>
              <p className={styles.faqAnswer}>
                <strong>Sim!</strong> Vá em Meus Afiliados → Ver detalhes → Editar Afiliação. 
                A nova comissão valerá <strong>apenas para vendas futuras</strong>, não 
                afeta vendas já realizadas.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>E se eu não tiver fornecedor ou representante?</span>
              <p className={styles.faqAnswer}>
                Sem problemas! Fornecedores e representantes são <strong>opcionais</strong>. 
                Se você não cadastrar nenhum, o sistema simplesmente <strong>pula essas deduções</strong> 
                e você recebe mais.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Como sei quanto vou receber de uma venda?</span>
              <p className={styles.faqAnswer}>
                Acesse <strong>Vendas → Relatórios</strong> ou <strong>Dashboard</strong> 
                para ver o valor líquido de cada venda. O sistema mostra exatamente 
                quanto foi deduzido e quanto você recebeu.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>Posso ter produtos com comissões diferentes?</span>
              <p className={styles.faqAnswer}>
                <strong>Sim!</strong> Cada produto pode ter fornecedores e representantes 
                diferentes em cada plano. E cada afiliado pode ter comissões diferentes 
                para cada produto que ele promove.
              </p>
            </div>
          </div>

          <div className={styles.faqItem}>
            <Icon icon="mdi:help-circle" className={styles.faqIcon} />
            <div>
              <span className={styles.faqQuestion}>A taxa de 4% da Pagmus pode ser negociada?</span>
              <p className={styles.faqAnswer}>
                A taxa padrão é de 4% para todos. Para <strong>grandes volumes</strong> ou 
                <strong>planos enterprise</strong>, entre em contato com nosso time comercial 
                através do suporte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dicas Importantes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas Importantes</span>
        </div>

        <Row className="g-3">
          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:check-circle" style={{ color: '#10b981', fontSize: '24px' }} />
              <div>
                <strong>Comece simples</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Não precisa cadastrar fornecedores e representantes se não tiver. 
                  Comece apenas com afiliados e adicione os outros depois.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:calculator" style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <strong>Simule antes</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Antes de definir comissões, calcule quanto vai sobrar para você. 
                  Lembre-se: 4% (taxa) + afiliado + fornecedor + rep. logístico.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:alert-circle" style={{ color: '#f59e0b', fontSize: '24px' }} />
              <div>
                <strong>Atenção aos custos</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Se a soma de todas as comissões ultrapassar o valor do produto, 
                  a venda não será lucrativa. Planeje bem!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard} style={{ padding: '20px' }}>
              <Icon icon="mdi:chart-line" style={{ color: '#8b5cf6', fontSize: '24px' }} />
              <div>
                <strong>Acompanhe os relatórios</strong>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  Use os relatórios de vendas para ver quais afiliados performam melhor 
                  e ajustar comissões se necessário.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default ComissoesWiki;

